import { useState, useEffect, useCallback, useRef } from 'react';
import { renderRegistryBlockView } from '../components/vision-deck/blockRegistry';
import '../components/vision-deck/registryTestBlock';
import '../components/vision-deck/blocks/FrameworkMatrixBlock';
import '../components/vision-deck/blocks/whiteboard/WhiteboardBlock';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Play, Maximize2, Minimize2, Send, CheckCircle2, Minus, Plus, SlidersHorizontal } from 'lucide-react';
import { renderPremiumExtraBlock } from '../components/vision-deck/PremiumExtraBlocks';
import { normalizeExternalUrl } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Google Ads Tracking ID
const GOOGLE_ADS_ID = 'AW-18004394710';

// Initialize Google Tag (gtag.js)
const initGoogleTag = () => {
  if (window.gtagInitialized) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GOOGLE_ADS_ID);
  window.gtagInitialized = true;
};

// Fire Vision_Deck_Viewed event
const fireGoogleAdsEvent = (deckInfo) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'Vision_Deck_Viewed', {
      deck_id: deckInfo.deck_id,
      deck_name: deckInfo.deck_name,
      company_name: deckInfo.company_name,
      template: deckInfo.template,
      send_to: GOOGLE_ADS_ID
    });
  }
};

const getSessionKey = (deckId) => `vision_deck_viewed_${deckId}`;
const hasViewedThisSession = (deckId) => {
  try { return sessionStorage.getItem(getSessionKey(deckId)) === 'true'; } catch { return false; }
};
const markViewedThisSession = (deckId) => {
  try { sessionStorage.setItem(getSessionKey(deckId), 'true'); } catch {}
};

// Template styles
const TEMPLATE_STYLES = {
  'modern-executive': {
    name: 'Modern Executive',
    background: 'linear-gradient(180deg, #FFFEF5 0%, #FAF9F4 100%)',
    solidBg: '#FFFEF5',
    text: '#1A1A1A',
    accent: '#B89B5E',
    secondaryBg: '#F5F5F0',
    muted: '#6B6B6B'
  },
  'contemporary-hospitality': {
    name: 'Contemporary Hospitality',
    background: 'linear-gradient(180deg, #FDF8F3 0%, #F7F0E8 100%)',
    solidBg: '#FDF8F3',
    text: '#3D3D3D',
    accent: '#8B7355',
    secondaryBg: '#F0E6D8',
    muted: '#7D7D7D'
  },
  'corporate-prestige': {
    name: 'Corporate Prestige',
    background: 'linear-gradient(180deg, #1A1A1A 0%, #0D0D0D 100%)',
    solidBg: '#1A1A1A',
    text: '#FFFFFF',
    accent: '#C9A962',
    secondaryBg: '#2D2D2D',
    muted: '#9A9A9A'
  }
};

// PAGE VISIBILITY MODES
const PAGE_MODES = {
  CONTINUOUS: 'continuous',    // All pages visible in vertical scroll
  HIDDEN: 'hidden',            // Page hidden until revealed
  BUTTON_NAV: 'button_nav'     // Page accessed via button click
};

// Premium Block Renderer
function PremiumBlock({ block, style, isFullscreen, onPageNavigate, pages, serviceAdjustments, onServiceAdjustmentChange }) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const getSpacingClasses = () => {
    switch (block.type) {
      case 'text':
        const size = block.content.size || 'text-base';
        if (size.includes('4xl') || size.includes('3xl')) return 'mb-5 md:mb-8';
        if (size.includes('2xl') || size.includes('xl')) return 'mb-4 md:mb-6';
        return 'mb-3 md:mb-5';
      case 'image':
      case 'video':
      case 'mobile-video':
        return 'mb-5 md:mb-8';
      case 'cta':
        return 'mb-4 md:mb-6 mt-3';
      case 'divider':
        return 'my-4 md:my-6';
      case 'logo':
        return 'mb-5 md:mb-8';
      default:
        return 'mb-4';
    }
  };

  const getFontSizeClass = (size) => {
    if (!isFullscreen) return size;
    const sizeMap = {
      'text-sm': 'text-base md:text-lg',
      'text-base': 'text-lg md:text-xl',
      'text-lg': 'text-xl md:text-2xl',
      'text-xl': 'text-2xl md:text-3xl',
      'text-2xl': 'text-3xl md:text-4xl',
      'text-3xl': 'text-4xl md:text-5xl',
      'text-4xl': 'text-5xl md:text-6xl lg:text-7xl',
    };
    return sizeMap[size] || size;
  };

  // Handle CTA button clicks - check for page link
  const handleCtaClick = (e) => {
    if (block.content.linkType === 'page' && block.content.targetPageId) {
      e.preventDefault();
      onPageNavigate?.(block.content.targetPageId);
    }
  };

  switch (block.type) {
    case 'text':
      return (
        <div className={`${getSpacingClasses()} w-full`}>
          <p 
            className={`whitespace-pre-wrap w-full ${block.content.font || 'font-sans'} ${getFontSizeClass(block.content.size || 'text-base')} ${block.content.bold ? 'font-bold' : ''} ${block.content.italic ? 'italic' : ''} ${block.content.underline ? 'underline' : ''} leading-relaxed tracking-wide`}
            style={{ 
              textAlign: block.content.align || 'left', 
              color: block.content.color || style.text
            }}
          >
            {block.content.content}
          </p>
        </div>
      );

    case 'image':
      return block.content.url ? (
        <div className={getSpacingClasses()}>
          <div 
            className={`rounded-xl md:rounded-2xl overflow-hidden ${block.content.frame?.shadow ? 'shadow-2xl' : ''} transform transition-transform duration-500 hover:scale-[1.02]`}
            style={{
              borderWidth: block.content.frame?.borderWidth || 0,
              borderColor: block.content.frame?.borderColor || '#B89B5E',
              borderStyle: 'solid',
              borderRadius: block.content.frame?.borderRadius || 16
            }}
          >
            <img src={block.content.url} alt={block.content.alt || 'Image'} className="w-full h-auto object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; if (e.target.parentNode) e.target.parentNode.style.display = 'none'; }} />
          </div>
        </div>
      ) : null;

    case 'video':
      if (isVideoPlaying || !block.content.thumbnailUrl) {
        return block.content.url ? (
          <div className={getSpacingClasses()}>
            <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-2xl" style={{ borderRadius: block.content.frame?.borderRadius || 16 }}>
              <video src={block.content.url} controls autoPlay={isVideoPlaying} className="w-full aspect-video object-contain bg-black" poster={block.content.thumbnailUrl} />
            </div>
          </div>
        ) : null;
      }
      return (
        <div className={getSpacingClasses()}>
          <div className="relative rounded-xl md:rounded-2xl overflow-hidden cursor-pointer group shadow-2xl" style={{ borderRadius: block.content.frame?.borderRadius || 16 }} onClick={() => setIsVideoPlaying(true)}>
            <img src={block.content.thumbnailUrl} alt="Video" className="w-full aspect-video object-cover transform transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-all duration-500">
              <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-white/95 flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-500">
                <Play className="w-10 h-10 md:w-14 md:h-14 text-[#B89B5E] ml-1" fill="currentColor" />
              </div>
            </div>
          </div>
        </div>
      );

    case 'mobile-video':
      if (isVideoPlaying || !block.content.thumbnailUrl) {
        return block.content.url ? (
          <div className={`${getSpacingClasses()} flex justify-center`}>
            <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-2xl max-w-md w-full" style={{ borderRadius: block.content.frame?.borderRadius || 16 }}>
              <video src={block.content.url} controls autoPlay={isVideoPlaying} playsInline className="w-full aspect-[9/16] object-cover bg-black" poster={block.content.thumbnailUrl} />
            </div>
          </div>
        ) : null;
      }
      return (
        <div className={`${getSpacingClasses()} flex justify-center`}>
          <div className="relative rounded-xl md:rounded-2xl overflow-hidden cursor-pointer group shadow-2xl max-w-md w-full" style={{ borderRadius: block.content.frame?.borderRadius || 16 }} onClick={() => setIsVideoPlaying(true)}>
            <img src={block.content.thumbnailUrl} alt="Video" className="w-full aspect-[9/16] object-cover transform transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-all duration-500">
              <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-500">
                <Play className="w-10 h-10 text-[#B89B5E] ml-1" fill="currentColor" />
              </div>
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-xs">Mobile Optimized</div>
          </div>
        </div>
      );

    case 'logo':
      return block.content.url ? (
        <div className={getSpacingClasses()} style={{ textAlign: block.content.align || 'center' }}>
          <img 
            src={block.content.url} 
            alt="Logo" 
            className={`inline-block transform transition-all duration-500 hover:scale-105 ${
              block.content.size === 'small' ? 'h-14 md:h-16' :
              block.content.size === 'large' ? 'h-32 md:h-40' : 'h-20 md:h-28'
            }`}
            onError={(e) => { e.target.style.display = 'none'; if (e.target.parentNode) e.target.parentNode.style.display = 'none'; }}
          />
        </div>
      ) : null;

    case 'divider':
      return (
        <div className={getSpacingClasses()} style={{ textAlign: 'center' }}>
          <hr className="mx-auto opacity-60" style={{ width: block.content.width || '60%', borderTopWidth: block.content.thickness || 1, borderTopStyle: block.content.style || 'solid', borderTopColor: block.content.color || style.muted }} />
        </div>
      );

    case 'cta':
      // Support page links and external links
      const isPageLink = block.content.linkType === 'page';
      const targetPage = isPageLink && pages ? pages.find(p => p.id === block.content.targetPageId) : null;
      
      return (
        <div className={getSpacingClasses()} style={{ textAlign: block.content.align || 'center' }}>
          {isPageLink ? (
            <button
              onClick={handleCtaClick}
              className={`inline-block px-8 md:px-12 py-4 md:py-5 rounded-xl font-semibold text-base md:text-lg tracking-wide transition-all duration-300 transform hover:scale-105 ${
                block.content.style === 'primary' ? 'text-white shadow-xl hover:shadow-2xl' : 'border-2 hover:shadow-lg'
              }`}
              style={{
                backgroundColor: block.content.style === 'primary' ? style.accent : 'transparent',
                borderColor: style.accent,
                color: block.content.style === 'primary' ? '#FFFFFF' : style.accent
              }}
            >
              {block.content.text || targetPage?.name || 'View More'}
            </button>
          ) : (
            <a
              href={normalizeExternalUrl(block.content.url)}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-block px-8 md:px-12 py-4 md:py-5 rounded-xl font-semibold text-base md:text-lg tracking-wide transition-all duration-300 transform hover:scale-105 ${
                block.content.style === 'primary' ? 'text-white shadow-xl hover:shadow-2xl' : 'border-2 hover:shadow-lg'
              }`}
              style={{
                backgroundColor: block.content.style === 'primary' ? style.accent : 'transparent',
                borderColor: style.accent,
                color: block.content.style === 'primary' ? '#FFFFFF' : style.accent
              }}
            >
              {block.content.text || 'Learn More'}
            </a>
          )}
        </div>
      );

    case 'service-adjuster':
      // Interactive Service Adjuster for viewers
      const content = block.content;
      const adjustment = serviceAdjustments?.[block.id] || {
        adjustedQuantity: content.adjustableQuantity || content.currentQuantity || 0,
        note: ''
      };
      
      const getIncrementValue = () => {
        switch (content.incrementMode) {
          case 'single': return 1;
          case 'custom': return content.customStep || 1;
          case '10': return 10;
          case '20': return 20;
          case '50': return 50;
          case '100': return 100;
          default: return 1;
        }
      };
      
      const increment = getIncrementValue();
      
      const handleQuantityChange = (delta) => {
        const currentQty = adjustment.adjustedQuantity;
        const minQty = content.minQuantity || 0;
        const maxQty = content.maxQuantity;

        // Snap-to-bound for legacy decks where current is outside [min, max].
        // Tap "+" while below min  → snap up to min (was a silent no-op).
        // Tap "-" while above max  → snap down to max.
        // Otherwise clamp normally.
        let newQty;
        if (delta > 0 && currentQty < minQty) {
          newQty = minQty;
        } else if (delta < 0 && maxQty !== null && maxQty !== undefined && currentQty > maxQty) {
          newQty = maxQty;
        } else {
          newQty = currentQty + (delta * increment);
          if (newQty < minQty) newQty = minQty;
          if (maxQty !== null && maxQty !== undefined && newQty > maxQty) newQty = maxQty;
        }

        if (newQty === currentQty) return;

        if (onServiceAdjustmentChange) {
          onServiceAdjustmentChange(block.id, {
            ...adjustment,
            adjustedQuantity: newQty
          });
        }
      };
      
      const handleNoteChange = (note) => {
        if (onServiceAdjustmentChange) {
          onServiceAdjustmentChange(block.id, {
            ...adjustment,
            note
          });
        }
      };
      
      const handleDirectInput = (e) => {
        const val = parseInt(e.target.value) || 0;
        const minQty = content.minQuantity || 0;
        const maxQty = content.maxQuantity;
        
        let newQty = val;
        if (newQty < minQty) newQty = minQty;
        if (maxQty !== null && maxQty !== undefined && newQty > maxQty) newQty = maxQty;
        
        if (onServiceAdjustmentChange) {
          onServiceAdjustmentChange(block.id, {
            ...adjustment,
            adjustedQuantity: newQty
          });
        }
      };
      
      return (
        <div className={`${getSpacingClasses()} w-full`}>
          <div 
            className="rounded-xl md:rounded-2xl p-5 md:p-6 border-2 transition-all duration-300"
            style={{ 
              backgroundColor: style.solidBg,
              borderColor: style.accent + '40'
            }}
          >
            {/* Service Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <SlidersHorizontal className="w-4 h-4" style={{ color: style.accent }} />
                  <h3 className="font-semibold text-lg" style={{ color: style.text }}>
                    {content.serviceName || 'Service'}
                  </h3>
                </div>
                {content.category && (
                  <p className="text-xs font-medium uppercase tracking-wider" style={{ color: style.muted }}>
                    {content.category}
                  </p>
                )}
              </div>
              {content.currentQuantity > 0 && (
                <div className="text-right">
                  <p className="text-xs" style={{ color: style.muted }}>Current Usage</p>
                  <p className="text-lg font-bold" style={{ color: style.text }}>
                    {content.currentQuantity} <span className="text-sm font-normal" style={{ color: style.muted }}>{content.unitLabel}</span>
                  </p>
                </div>
              )}
            </div>
            
            {/* Description */}
            {content.description && (
              <p className="text-sm mb-4" style={{ color: style.muted }}>
                {content.description}
              </p>
            )}
            
            {/* Quantity Adjuster */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4" style={{ position: 'relative', zIndex: 10 }}>
              <span className="text-sm font-medium" style={{ color: style.text }}>Adjust Quantity:</span>
              <div className="flex items-center gap-3" style={{ touchAction: 'manipulation', position: 'relative', zIndex: 20 }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleQuantityChange(-1);
                  }}
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl font-bold shadow-lg active:scale-90 active:opacity-80 transition-all select-none"
                  style={{ 
                    backgroundColor: style.accent,
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: 30
                  }}
                  aria-label="Decrease quantity"
                  data-testid="adjuster-minus-btn"
                >
                  <Minus className="w-6 h-6 pointer-events-none" />
                </button>
                {/* MOBILE FIX (P1c):
                    - readOnly + inputMode="none" → tapping NEVER opens the mobile keyboard
                    - tabIndex=-1 → not focusable so accidental edge-taps cannot land here
                    - displays the current quantity; +/- buttons are the sole input mechanism */}
                <div
                  role="status"
                  aria-live="polite"
                  aria-label="Current quantity"
                  className="w-24 h-14 flex items-center justify-center font-bold text-2xl rounded-xl border-2 select-none"
                  style={{ 
                    borderColor: style.accent + '60',
                    color: style.text,
                    backgroundColor: style.background || '#fff',
                    position: 'relative',
                    zIndex: 25,
                    touchAction: 'manipulation',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                  data-testid="adjuster-quantity-display"
                >
                  {adjustment.adjustedQuantity}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleQuantityChange(1);
                  }}
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl font-bold shadow-lg active:scale-90 active:opacity-80 transition-all select-none"
                  style={{ 
                    backgroundColor: style.accent,
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: 30
                  }}
                  aria-label="Increase quantity"
                  data-testid="adjuster-plus-btn"
                >
                  <Plus className="w-6 h-6 pointer-events-none" />
                </button>
                <span className="text-sm ml-1" style={{ color: style.muted }}>{content.unitLabel}</span>
              </div>
            </div>
            <div className="text-xs mb-4" style={{ color: style.muted }}>(+/- {increment})</div>
            
            {/* Change Indicator */}
            {adjustment.adjustedQuantity !== (content.adjustableQuantity || content.currentQuantity || 0) && (
              <div className="mb-4 p-2 rounded-lg text-sm" style={{ backgroundColor: style.accent + '10', color: style.accent }}>
                <span className="font-medium">Change: </span>
                {(content.adjustableQuantity || content.currentQuantity || 0)} → {adjustment.adjustedQuantity} {content.unitLabel}
                {adjustment.adjustedQuantity > (content.adjustableQuantity || content.currentQuantity || 0) 
                  ? ` (+${adjustment.adjustedQuantity - (content.adjustableQuantity || content.currentQuantity || 0)})` 
                  : ` (${adjustment.adjustedQuantity - (content.adjustableQuantity || content.currentQuantity || 0)})`}
              </div>
            )}
            
            {/* Prospect Note */}
            {content.allowProspectNote && (
              <div>
                <label className="text-xs font-medium block mb-2" style={{ color: style.muted }}>Add a note (optional)</label>
                <textarea
                  value={adjustment.note}
                  onChange={(e) => handleNoteChange(e.target.value)}
                  placeholder="Any special requests or comments..."
                  className="w-full p-4 rounded-lg border-2 text-base resize-none focus:outline-none focus:ring-2 touch-manipulation"
                  style={{ 
                    borderColor: style.accent + '30',
                    backgroundColor: style.background || '#fff',
                    color: style.text,
                    minHeight: '80px'
                  }}
                  rows={3}
                />
              </div>
            )}
          </div>
        </div>
      );

    case 'testimonial':
    case 'stats':
    case 'contact-card':
    case 'comparison':
    case 'countdown':
    case 'pricing':
    case 'timeline':
    case 'faq':
    case 'roi-calculator':
    case 'booking':
    case 'lead-form':
    case 'video-testimonial':
      return renderPremiumExtraBlock(block, style);

    default:
      // Universal Builder: delegate unknown types to the additive registry.
      // Falls back to null (graceful) when the type is not registered.
      return renderRegistryBlockView(block, style);
  }
}

// Page Section Component for continuous scroll
function PageSection({ page, pageIndex, style, isFullscreen, onPageNavigate, pages, isDark, serviceAdjustments, onServiceAdjustmentChange }) {
  const sectionRef = useRef(null);
  
  return (
    <section 
      ref={sectionRef}
      id={`page-${page.id}`}
      className="flex flex-col items-stretch justify-start px-6 md:px-12 lg:px-20 py-8 md:py-10"
      style={{ background: pageIndex % 2 === 0 ? style.background : style.solidBg }}
    >
      <div className="w-full max-w-5xl mx-auto">
        {page.blocks?.length > 0 ? (
          page.blocks.map((block) => (
            <PremiumBlock 
              key={block.id} 
              block={block} 
              style={style}
              isFullscreen={isFullscreen}
              onPageNavigate={onPageNavigate}
              pages={pages}
              serviceAdjustments={serviceAdjustments}
              onServiceAdjustmentChange={onServiceAdjustmentChange}
            />
          ))
        ) : (
          <div className="flex items-center justify-center min-h-[50vh]">
            <p style={{ color: style.muted }} className="text-xl">This page is empty</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default function PublicDeckView() {
  const { deckId } = useParams();
  const [deck, setDeck] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [layoutMode, setLayoutMode] = useState('continuous'); // continuous | paginated
  const [visiblePages, setVisiblePages] = useState(new Set());
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const trackingFired = useRef(false);
  
  // Service Adjuster state
  const [serviceAdjustments, setServiceAdjustments] = useState({});
  const [hasServiceAdjusterBlocks, setHasServiceAdjusterBlocks] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');

  useEffect(() => { initGoogleTag(); }, []);
  useEffect(() => { fetchDeck(); }, [deckId]);
  
  // Check for service adjuster blocks - only initialize once
  const adjustmentsInitialized = useRef(false);
  
  useEffect(() => {
    if (pages.length > 0 && !adjustmentsInitialized.current) {
      const hasBlocks = pages.some(page => 
        page.blocks?.some(block => block.type === 'service-adjuster')
      );
      setHasServiceAdjusterBlocks(hasBlocks);
      
      // Initialize adjustments state for service adjuster blocks (only once)
      if (hasBlocks) {
        const initialAdjustments = {};
        pages.forEach(page => {
          page.blocks?.forEach(block => {
            if (block.type === 'service-adjuster') {
              initialAdjustments[block.id] = {
                adjustedQuantity: block.content.adjustableQuantity || block.content.currentQuantity || 0,
                note: ''
              };
            }
          });
        });
        setServiceAdjustments(initialAdjustments);
        adjustmentsInitialized.current = true;
      }
    }
  }, [pages]);
  
  // Handle service adjustment change
  const handleServiceAdjustmentChange = useCallback((blockId, adjustment) => {
    setServiceAdjustments(prev => ({
      ...prev,
      [blockId]: adjustment
    }));
  }, []);
  
  // Submit all service adjustments
  const handleSubmitAdjustments = async () => {
    setSubmitting(true);
    try {
      // Compile all service adjuster block data
      const adjustments = [];
      pages.forEach(page => {
        page.blocks?.forEach(block => {
          if (block.type === 'service-adjuster') {
            const adj = serviceAdjustments[block.id];
            adjustments.push({
              block_id: block.id,
              service_name: block.content.serviceName,
              category: block.content.category || '',
              description: block.content.description || '',
              current_quantity: block.content.currentQuantity || 0,
              adjusted_quantity: adj?.adjustedQuantity || block.content.adjustableQuantity || 0,
              unit_label: block.content.unitLabel || 'units',
              recipient_note: adj?.note || ''
            });
          }
        });
      });
      
      const response = await fetch(`${API}/api/vision-decks/${deckId}/service-adjustments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adjustments,
          recipient_name: recipientName || null,
          recipient_email: recipientEmail || null
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit adjustments');
      }
      
      setSubmitSuccess(true);
    } catch (err) {
      console.error('Failed to submit adjustments:', err);
      alert('Failed to submit your updates. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Determine layout mode from deck settings
  useEffect(() => {
    if (deck?.settings?.layoutMode) {
      setLayoutMode(deck.settings.layoutMode);
    } else {
      // Default to continuous scroll
      setLayoutMode('continuous');
    }
    
    // Initialize visible pages based on page settings
    if (pages.length > 0) {
      const visible = new Set();
      pages.forEach((page, index) => {
        const mode = page.visibility || PAGE_MODES.CONTINUOUS;
        if (mode === PAGE_MODES.CONTINUOUS || index === 0) {
          visible.add(page.id);
        }
      });
      setVisiblePages(visible);
    }
  }, [deck, pages]);

  // Auto-hide controls
  useEffect(() => {
    let timeout;
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => { window.removeEventListener('mousemove', handleMouseMove); clearTimeout(timeout); };
  }, []);

  const fetchDeck = async () => {
    try {
      const response = await fetch(`${API}/api/public/vision-decks/${deckId}`);
      if (response.ok) {
        const data = await response.json();
        setDeck(data);
        setPages(data.pages || []);

        // The view-counter POST fires on EVERY fresh page-load (a new tab is
        // a new "view" — that's what recipients + owners expect). Session
        // dedup applies ONLY to the ad-attribution events below so we
        // don't inflate Google Ads / engagement metrics on the same tab.
        try {
          await fetch(`${API}/api/public/vision-decks/${deckId}/view`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
        } catch (err) {
          console.error('Failed to increment deck view counter:', err);
        }

        if (!trackingFired.current && !hasViewedThisSession(deckId)) {
          trackingFired.current = true;
          fireGoogleAdsEvent({
            deck_id: deckId,
            deck_name: data.name || 'Untitled Deck',
            company_name: data.opportunity_name || data.company_name || '',
            template: data.template_id || 'modern-executive'
          });
          trackEngagement(data);
          markViewedThisSession(deckId);
        }
      } else if (response.status === 404) {
        setError('This vision deck was not found or is no longer available.');
      } else if (response.status === 403) {
        setError('This vision deck is not published yet.');
      } else {
        setError('Unable to load this vision deck. Please try again later.');
      }
    } catch (err) {
      setError('Unable to connect. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const trackEngagement = async (deckData) => {
    // NOTE: The view-counter POST is fired UNCONDITIONALLY in fetchDeck
    // (above). This function is dedup-guarded — it fires the
    // Google-Ads-style structured attribution event only once per
    // browser session so external ad platforms don't double-count.
    try {
      await fetch(`${API}/api/public/vision-decks/${deckId}/engagement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'Vision_Deck_Viewed',
          deck_name: deckData.name || 'Untitled Deck',
          template_name: TEMPLATE_STYLES[deckData.template_id]?.name || 'Modern Executive',
          referrer: document.referrer,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      });
    } catch (err) {
      console.error('Failed to track deck engagement:', err);
    }
  };

  // Navigate to a specific page (for page-link buttons)
  const navigateToPage = useCallback((pageId) => {
    // Make the page visible
    setVisiblePages(prev => new Set([...prev, pageId]));
    
    // Scroll to the page
    setTimeout(() => {
      const element = document.getElementById(`page-${pageId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  // Paginated navigation
  const goToPage = useCallback((direction) => {
    if (direction === 'prev' && currentPageIndex > 0) {
      setCurrentPageIndex(prev => prev - 1);
    } else if (direction === 'next' && currentPageIndex < pages.length - 1) {
      setCurrentPageIndex(prev => prev + 1);
    }
  }, [currentPageIndex, pages.length]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-gray-400 text-lg tracking-wide">Loading presentation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D] p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-red-900/30 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-3">Unable to Load</h1>
          <p className="text-gray-400 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  const templateStyle = deck?.template_id || 'modern-executive';
  const style = TEMPLATE_STYLES[templateStyle];
  const isDark = templateStyle === 'corporate-prestige';
  const useContinuousScroll = layoutMode === 'continuous';

  // CONTINUOUS SCROLL MODE
  if (useContinuousScroll) {
    return (
      <div className="min-h-screen" style={{ background: style.background }}>
        {/* Minimal Header */}
        <header 
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
            showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'
          }`}
          style={{ background: isDark ? 'rgba(13,13,13,0.9)' : 'rgba(255,255,255,0.9)' }}
        >
          <div className="px-6 py-4 flex items-center justify-between backdrop-blur-sm">
            <h1 className="text-sm md:text-base font-medium tracking-widest uppercase" style={{ color: style.muted }}>
              {deck?.name || 'Vision Deck'}
            </h1>
            <button onClick={toggleFullscreen} className="p-2 rounded-lg transition-colors hover:bg-black/10" style={{ color: style.muted }}>
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </header>

        {/* Continuous Scroll Content */}
        <main>
          {pages.map((page, index) => {
            const pageMode = page.visibility || PAGE_MODES.CONTINUOUS;
            const isVisible = pageMode === PAGE_MODES.CONTINUOUS || visiblePages.has(page.id) || index === 0;
            
            if (!isVisible) return null;
            
            return (
              <PageSection 
                key={page.id}
                page={page}
                pageIndex={index}
                style={style}
                isFullscreen={isFullscreen}
                onPageNavigate={navigateToPage}
                pages={pages}
                isDark={isDark}
                serviceAdjustments={serviceAdjustments}
                onServiceAdjustmentChange={handleServiceAdjustmentChange}
              />
            );
          })}
          
          {/* Service Adjuster Submission Section */}
          {hasServiceAdjusterBlocks && !submitSuccess && (
            <section 
              className="py-16 px-6 md:px-12 lg:px-20"
              style={{ background: style.solidBg }}
            >
              <div className="w-full max-w-2xl mx-auto">
                <div 
                  className="rounded-2xl p-6 md:p-8 border-2"
                  style={{ borderColor: style.accent, backgroundColor: style.background }}
                >
                  <div className="text-center mb-6">
                    <Send className="w-10 h-10 mx-auto mb-3" style={{ color: style.accent }} />
                    <h3 className="text-xl font-semibold mb-2" style={{ color: style.text }}>
                      Ready to Submit Your Updates?
                    </h3>
                    <p className="text-sm" style={{ color: style.muted }}>
                      Review your quantity adjustments above, then submit to send them to us.
                    </p>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="text-sm font-medium block mb-1" style={{ color: style.text }}>Your Name (optional)</label>
                      <input
                        type="text"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="Enter your name"
                        className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none"
                        style={{ borderColor: style.accent + '40', backgroundColor: style.solidBg, color: style.text }}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1" style={{ color: style.text }}>Your Email (optional)</label>
                      <input
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none"
                        style={{ borderColor: style.accent + '40', backgroundColor: style.solidBg, color: style.text }}
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSubmitAdjustments}
                    disabled={submitting}
                    className="w-full py-4 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg disabled:opacity-50"
                    style={{ backgroundColor: style.accent }}
                  >
                    {submitting ? 'Sending...' : 'Send Updated Quantities'}
                  </button>
                </div>
              </div>
            </section>
          )}
          
          {/* Submission Success Message */}
          {hasServiceAdjusterBlocks && submitSuccess && (
            <section 
              className="py-16 px-6 md:px-12 lg:px-20"
              style={{ background: style.solidBg }}
            >
              <div className="w-full max-w-2xl mx-auto text-center">
                <div 
                  className="rounded-2xl p-8 md:p-12 border-2"
                  style={{ borderColor: style.accent, backgroundColor: style.background }}
                >
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <h3 className="text-2xl font-semibold mb-2" style={{ color: style.text }}>
                    Your updates have been sent successfully!
                  </h3>
                  <p className="text-sm" style={{ color: style.muted }}>
                    We've received your quantity adjustments and will be in touch soon.
                  </p>
                </div>
              </div>
            </section>
          )}
        </main>

        {/* Page Navigation Dots intentionally removed — continuous scroll mode does not need them */}

        {/* Scroll Indicator (first page only) */}
        {pages.length > 1 && (
          <div 
            className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${
              showControls ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="flex flex-col items-center gap-2 animate-bounce">
              <ChevronDown className="w-6 h-6" style={{ color: style.muted }} />
              {/* Feb 2026 — "SCROLL" label removed (was overlapping the
                  chevron and bleeding through over CTA / pricing sections
                  on the public deck). Chevron alone communicates the hint. */}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="py-8 text-center" style={{ background: isDark ? '#0D0D0D' : style.solidBg }}>
          <p className="text-xs tracking-widest uppercase" style={{ color: style.muted }}>
            Powered by DH DESIGN
          </p>
        </footer>
      </div>
    );
  }

  // PAGINATED MODE (Original swipe/click navigation)
  const currentPage = pages[currentPageIndex];
  
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: isDark ? '#0D0D0D' : '#F5F4F0' }}>
      <div className="min-h-screen flex flex-col" style={{ background: style.background }}>
        {/* Header */}
        <header 
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
            showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'
          }`}
          style={{ background: isDark ? 'rgba(13,13,13,0.9)' : 'rgba(255,255,255,0.9)' }}
        >
          <div className="px-6 py-4 flex items-center justify-between backdrop-blur-sm">
            <h1 className="text-sm md:text-base font-medium tracking-widest uppercase" style={{ color: style.muted }}>
              {deck?.name || 'Vision Deck'}
            </h1>
            <button onClick={toggleFullscreen} className="p-2 rounded-lg transition-colors hover:bg-black/10" style={{ color: style.muted }}>
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-20 py-20 md:py-24">
          <div className="w-full max-w-5xl mx-auto">
            {currentPage?.blocks?.length > 0 ? (
              currentPage.blocks.map((block) => (
                <PremiumBlock 
                  key={block.id} 
                  block={block} 
                  style={style}
                  isFullscreen={isFullscreen}
                  onPageNavigate={navigateToPage}
                  pages={pages}
                />
              ))
            ) : (
              <div className="flex items-center justify-center min-h-[50vh]">
                <p style={{ color: style.muted }} className="text-xl">This page is empty</p>
              </div>
            )}
          </div>
        </main>

        {/* Navigation Controls */}
        {pages.length > 1 && (
          <nav 
            className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-500 ${
              showControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'
            }`}
            style={{ background: isDark ? 'rgba(13,13,13,0.9)' : 'rgba(255,255,255,0.9)' }}
          >
            <div className="px-6 py-4 flex items-center justify-center gap-8 backdrop-blur-sm">
              <button
                onClick={() => goToPage('prev')}
                disabled={currentPageIndex === 0}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110"
                style={{ background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', color: style.text }}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <div className="flex items-center gap-3">
                {pages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPageIndex(index)}
                    className={`rounded-full transition-all duration-300 ${currentPageIndex === index ? 'w-10 h-3' : 'w-3 h-3 hover:scale-125'}`}
                    style={{ background: currentPageIndex === index ? style.accent : style.muted, opacity: currentPageIndex === index ? 1 : 0.5 }}
                  />
                ))}
              </div>
              
              <button
                onClick={() => goToPage('next')}
                disabled={currentPageIndex === pages.length - 1}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110"
                style={{ background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', color: style.text }}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
            
            <div className="pb-4 text-center">
              <p className="text-xs tracking-widest uppercase" style={{ color: style.muted }}>
                {currentPageIndex + 1} / {pages.length}
                <span className="hidden md:inline ml-4 opacity-60">Press ← → to navigate • F for fullscreen</span>
              </p>
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}
